import {
  Box,
  Button,
  Stack,
  Switch,
  SxProps,
  Typography,
  debounce,
  useMediaQuery,
} from "@mui/material";
import { Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";
import {
  FeedbackData,
  FeedbackLoggingPayload,
  PaneType,
  SelectionSource,
} from "../features/api/logging";
import PatientList, { getPatientListItemId } from "../components/PatientList";
import TaskList, { getTaskListItemId } from "../components/TaskList";
import {
  ViewingMode,
  selectChatOpenUxState,
  selectFeedbackDialogInfo,
  selectHasUnsavedNote,
  selectHasUnsavedWork,
  selectViewingMode,
  setChatOpenUxState,
  setViewingMode,
} from "../features/appState/uxSlice";
import { createStyleSheet, globalStyles } from "../utils/styles";
import {
  selectGroupByPatientState,
  selectTaskListShowActiveState,
  setGroupByPatient,
  setTaskListShowActive,
} from "../features/appState/userPrefsSlice";
import { useCallback, useMemo, useState } from "react";

import CheckButtonGroup from "../components/CheckButtonGroup";
import ClearSearchIcon from "@mui/icons-material/Cancel";
import EmptyPage from "../../assets/select_task.png";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import IconButton from "../components/IconButton";
import NavNextIcon from "@mui/icons-material/ChevronRight";
import NavPreviousIcon from "@mui/icons-material/ChevronLeft";
import React from "react";
import SearchInput from "../components/SearchInput";
import { SerializedError } from "@reduxjs/toolkit";
import TabView from "../components/TabView";
import TaskListIcon from "@mui/icons-material/MenuOutlined";
import { TaskState } from "../features/data/patientApiSlice";
import UnsavedChangesDialog from "../components/UnsavedChangesDialog";
import { getErrorText } from "../utils/errors";
import { logEvent } from "../features/api/apiSlice";
import { theme } from "../../theme";
import { store, useAppDispatch } from "../features/store";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import FeedbackDialog from "../components/FeedbackDialog";
import {
  selectCurrentEmail,
  selectCurrentName,
  selectCurrentPatientGroup,
} from "../features/auth/authSlice";
import { useDeviceType } from "../utils/logging";

export enum FilterType {
  ReadyToChat = "ReadyToChat",
  Concerns = "Concerns",
  FollowUps = "FollowUps",
}

const allFilters: Set<FilterType> = new Set([
  FilterType.ReadyToChat,
  FilterType.Concerns,
  FilterType.FollowUps,
]);

export type FilterTypeInfo = {
  count: number;
  flaggedForSupervisor: boolean;
};

type QueryError = FetchBaseQueryError | SerializedError | undefined;
export type QueryStateUpdateFn = (
  listIsEmpty: boolean,
  isFetching: boolean,
  isError: boolean,
  error: QueryError
) => void;

const Patients = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { t } = useTranslation("Patients");
  const dispatch = useAppDispatch();
  const patientSelected = useMatch("/patients/patient/:uid");
  const isPortrait = useMediaQuery("(orientation: portrait)");

  // Component state
  const chatOpen = useSelector(selectChatOpenUxState);
  const groupByPatient = useSelector(selectGroupByPatientState);
  const taskListShowActive = useSelector(selectTaskListShowActiveState);
  const feedbackFormOpen = useSelector(selectFeedbackDialogInfo);
  const userEmail = useSelector(selectCurrentEmail);
  const userName = useSelector(selectCurrentName);
  const patientGroup = useSelector(selectCurrentPatientGroup);
  const [searchValue, setSearchValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<Set<FilterType>>(
    new Set([FilterType.ReadyToChat, FilterType.Concerns, FilterType.FollowUps])
  );
  const [typeCounts, setTypeCounts] = useState<Map<FilterType, FilterTypeInfo>>(
    new Map<FilterType, FilterTypeInfo>()
  );
  const [unsavedChangesDialogInfo, setUnsavedChangesDialogInfo] = useState<{
    open: boolean;
    patientUid?: string;
    taskUid?: string;
    selectionSource?: SelectionSource;
  }>({ open: false });
  const [queryState, setQueryState] = useState<{
    listIsEmpty: boolean;
    isFetching: boolean;
    isError: boolean;
    error: QueryError;
  }>({
    listIsEmpty: true,
    isFetching: false,
    isError: false,
    error: undefined,
  });
  const [listNavControls, setListNavControls] = useState<{
    navPrevious: () => void;
    navNext: () => void;
  }>({
    navPrevious: () => {},
    navNext: () => {},
  });
  const [listNavState, updateListNavState] = useState<{
    canNavPrevious: boolean;
    canNavNext: boolean;
  }>({
    canNavPrevious: false,
    canNavNext: false,
  });
  const userDeviceType = useDeviceType();

  // Callbacks
  const handleQueryStateUpdate = (
    listIsEmpty: boolean,
    isFetching: boolean,
    isError: boolean,
    error: QueryError
  ) => {
    if (
      listIsEmpty !== queryState.listIsEmpty ||
      isFetching !== queryState.isFetching ||
      isError !== queryState.isError ||
      error !== queryState.error
    ) {
      setQueryState({ listIsEmpty, isFetching, isError, error });
    }
  };
  const handleTaskPaneClick = useCallback(() => {
    logEvent(dispatch, {
      event: "paneOpened",
      data: { pane: PaneType.TaskList },
    });
    dispatch(setChatOpenUxState(false));
  }, [dispatch]);
  const debouncedSearchTermChange = useMemo(
    () =>
      debounce((value: string) => {
        logEvent(dispatch, {
          event: "search",
          data: { hasSearchTerm: !!value.trim() },
        });
        setSearchTerm(value.trim().toLowerCase());
      }, 400),
    [dispatch]
  );
  const onSearchChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(target.value);
    debouncedSearchTermChange(target.value);
  };
  const handleSearchClearClick = useCallback(() => {
    logEvent(dispatch, {
      event: "search",
      data: { hasSearchTerm: false },
    });
    setSearchValue("");
    setSearchTerm("");
  }, [dispatch]);
  const handleGroupByPatientChange = useCallback(() => {
    logEvent(dispatch, {
      event: "groupByPatientSelected",
      data: { selected: !groupByPatient },
    });
    dispatch(setGroupByPatient(!groupByPatient));
  }, [groupByPatient, dispatch]);
  const handleFilterClick = useCallback(
    (filter: FilterType) => () => {
      const newFilters = new Set(filters);
      if (newFilters.has(filter)) {
        newFilters.delete(filter);
      } else {
        newFilters.add(filter);
      }
      setFilters(newFilters);
      logEvent(dispatch, {
        event: "filterButtonToggled",
        data: {
          filterType: filter,
          resultingFilters: Array.from(newFilters).map((k) => k),
        },
      });
    },
    [filters, dispatch]
  );
  const handleShowOnlyActiveTasksClicked = useCallback(
    (showActive: boolean) => () => {
      dispatch(setTaskListShowActive(showActive));
      logEvent(dispatch, {
        event: "taskStateSelected",
        data: { state: showActive ? TaskState.todo : TaskState.complete },
      });
    },
    [dispatch]
  );
  const selectTask = useCallback(
    (patientUid: string, taskUid: string, selectionSource: SelectionSource) => {
      logEvent(dispatch, {
        event: "taskSelected",
        data: { taskUid, selectionSource },
      });
      if (selectionSource === SelectionSource.TaskList) {
        dispatch(setViewingMode(ViewingMode.Tasks));
      }
      navigate(`/patients/patient/${patientUid}`, {
        state: { taskUid, unsavedWorkDiscarded: true },
      });
      const element = groupByPatient
        ? document.getElementById(getPatientListItemId(patientUid))
        : document.getElementById(getTaskListItemId(taskUid));
      element?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    },
    [navigate, dispatch, groupByPatient]
  );
  const handleTaskSelect = useCallback(
    (patientUid: string, taskUid: string, selectionSource: SelectionSource) => {
      // setViewingMode runs before navigate, so the router's blocker would fire too late.
      const state = store.getState();
      const losesUnsavedWork =
        patientUid !== patientSelected?.params.uid
          ? selectHasUnsavedWork(state)
          : selectionSource === SelectionSource.TaskList &&
            selectViewingMode(state) !== ViewingMode.Tasks &&
            selectHasUnsavedNote(state);
      if (losesUnsavedWork) {
        setUnsavedChangesDialogInfo({
          open: true,
          patientUid,
          taskUid,
          selectionSource,
        });
      } else {
        selectTask(patientUid, taskUid, selectionSource);
      }
    },
    [patientSelected, selectTask]
  );
  const handleUnsavedChangesDialogClose = useCallback(() => {
    setUnsavedChangesDialogInfo({ open: false });
  }, []);
  const handleUnsavedChangesDialogDiscard = useCallback(() => {
    setUnsavedChangesDialogInfo({ open: false });
    selectTask(
      unsavedChangesDialogInfo.patientUid!,
      unsavedChangesDialogInfo.taskUid!,
      unsavedChangesDialogInfo.selectionSource!
    );
  }, [selectTask, unsavedChangesDialogInfo]);
  const handleFeedbackOnSubmit = (feedbackData: FeedbackData) => {
    const payload: FeedbackLoggingPayload = {
      feedback: feedbackData,
      deviceType: userDeviceType,
      userAgent: navigator.userAgent,
      url: window.location.href,
      userName,
      userEmail,
      patientGroup: patientGroup?.[userEmail],
      patientId: patientSelected?.params.uid || "",
    };
    logEvent(dispatch, {
      event: "userFeedback",
      data: payload,
    });
  };

  const disableFromSearchTerm = searchTerm.length > 0;
  return isPortrait ? (
    <Box
      sx={
        {
          ...styles.container,
          ...styles.landscapeOnlyContainer,
        } as SxProps
      }
    >
      <Typography variant="body1" sx={globalStyles.gapTop}>
        {t("landscapeOnly")}
      </Typography>
    </Box>
  ) : (
    <Box sx={styles.container}>
      <Box
        sx={{
          ...styles.leftPane,
          width: !chatOpen ? theme.spacing(56) : theme.spacing(10),
          [theme.breakpoints.down("desktop")]: {
            width: !chatOpen ? theme.spacing(40) : theme.spacing(7),
          },
        }}
      >
        <Box
          sx={{
            ...styles.collapsedPaneContainer,
            opacity: !chatOpen ? 0 : 1,
            pointerEvents: !chatOpen ? "none" : "auto",
          }}
        >
          <Box sx={styles.buttonContainer}>
            <IconButton
              icon={<TaskListIcon />}
              tooltip={t("common:expand")}
              onClick={handleTaskPaneClick}
            />
          </Box>
          <Typography variant="filterTypeTitleMini">
            {typeCounts.get(FilterType.ReadyToChat)?.count || 0}
          </Typography>
          <Typography variant="filterTypeSubtitleMini">
            {t("chatsMini")}
          </Typography>
          <Typography variant="filterTypeTitleMini">
            {typeCounts.get(FilterType.Concerns)?.count || 0}
          </Typography>
          <Typography variant="filterTypeSubtitleMini">
            {t("concernsMini")}
          </Typography>
          <Typography variant="filterTypeTitleMini">
            {typeCounts.get(FilterType.FollowUps)?.count || 0}
          </Typography>
          <Typography variant="filterTypeSubtitleMini">
            {t("followUpsMini")}
          </Typography>
        </Box>
        <Box
          sx={{
            ...styles.paneContainer,
            opacity: !chatOpen ? 1 : 0,
            pointerEvents: !chatOpen ? "auto" : "none",
          }}
        >
          <SearchInput
            inputProps={{
              placeholder: t("searchPlaceholder"),
              value: searchValue,
              endAdornment: searchValue ? (
                <IconButton
                  icon={<ClearSearchIcon />}
                  tooltip={t("common:clear")}
                  onClick={handleSearchClearClick}
                  sx={{
                    marginRight: 1,
                    [theme.breakpoints.down("desktop")]: {
                      marginRight: 2,
                    },
                  }}
                />
              ) : undefined,
              onChange: onSearchChange,
            }}
            sx={{
              [theme.breakpoints.down("desktop")]: {
                height: 36,
              },
            }}
          />
          <Stack direction="row" sx={styles.groupByPatientRow}>
            <Typography variant="button">{t("groupByPatient")}</Typography>
            <Switch
              checked={groupByPatient}
              onChange={handleGroupByPatientChange}
            />
          </Stack>
          <CheckButtonGroup
            buttons={[
              {
                label: t("readyToChat"),
                count: typeCounts.get(FilterType.ReadyToChat)?.count || 0,
                flagged:
                  !!typeCounts.get(FilterType.ReadyToChat)
                    ?.flaggedForSupervisor || false,
                onClick: handleFilterClick(FilterType.ReadyToChat),
                selected: filters.has(FilterType.ReadyToChat),
              },
              {
                label: t("concerns"),
                count: typeCounts.get(FilterType.Concerns)?.count || 0,
                flagged:
                  !!typeCounts.get(FilterType.Concerns)?.flaggedForSupervisor ||
                  false,
                onClick: handleFilterClick(FilterType.Concerns),
                selected: filters.has(FilterType.Concerns),
              },
              {
                label: t("followUps"),
                count: typeCounts.get(FilterType.FollowUps)?.count || 0,
                flagged:
                  !!typeCounts.get(FilterType.FollowUps)
                    ?.flaggedForSupervisor || false,
                onClick: handleFilterClick(FilterType.FollowUps),
                selected: filters.has(FilterType.FollowUps),
              },
            ]}
            disabled={disableFromSearchTerm}
          />
          <TabView
            entries={[
              {
                content: t("active"),
                selected: taskListShowActive,
                onClick: handleShowOnlyActiveTasksClicked(true),
              },
              {
                content: t("completed"),
                selected: !taskListShowActive,
                onClick: handleShowOnlyActiveTasksClicked(false),
              },
            ]}
            sx={globalStyles.gapTop}
            disabled={disableFromSearchTerm}
            postTabComponent={
              filters.size > 0 &&
              !queryState.listIsEmpty &&
              !disableFromSearchTerm ? (
                <Typography
                  variant="detailCellSubtitle"
                  sx={styles.sortOrderText}
                >
                  {t(taskListShowActive ? "oldestAtTop" : "mostRecentAtTop")}
                </Typography>
              ) : null
            }
          />
          <Box sx={styles.taskListContainer}>
            {queryState.isFetching && queryState.listIsEmpty ? (
              <Typography variant="body1" sx={globalStyles.gapTop}>
                {t("common:loading")}
              </Typography>
            ) : queryState.isError ? (
              <Typography variant="errorBody" sx={globalStyles.gapTop}>
                {getErrorText(queryState.error)}
              </Typography>
            ) : filters.size === 0 || queryState.listIsEmpty ? (
              <Typography variant="body1" sx={globalStyles.gapTop}>
                {t("noTasksWithFilters")}
              </Typography>
            ) : null}
            {groupByPatient ? (
              <PatientList
                showOnlyActiveTasks={taskListShowActive}
                filters={!disableFromSearchTerm ? filters : allFilters}
                setTypeCounts={setTypeCounts}
                searchTerm={searchTerm}
                selectedPatientUid={patientSelected?.params.uid || ""}
                onQueryStateUpdate={handleQueryStateUpdate}
                onTaskSelect={handleTaskSelect}
                setListNavControls={setListNavControls}
                updateListNavState={updateListNavState}
              />
            ) : (
              <TaskList
                showOnlyActiveTasks={taskListShowActive}
                filters={!disableFromSearchTerm ? filters : allFilters}
                searchTerm={searchTerm}
                selectedTaskUid={state?.taskUid || ""}
                setTypeCounts={setTypeCounts}
                onQueryStateUpdate={handleQueryStateUpdate}
                onTaskSelect={handleTaskSelect}
                setListNavControls={setListNavControls}
                updateListNavState={updateListNavState}
              />
            )}
          </Box>
        </Box>
      </Box>
      <Box sx={styles.topPane}>
        <Box sx={styles.navButtonContainer}>
          {patientSelected ? (
            <>
              <Button
                variant="text"
                onClick={listNavControls.navPrevious}
                disabled={!listNavState.canNavPrevious}
              >
                <NavPreviousIcon
                  sx={{
                    fontSize: "32px",
                    [theme.breakpoints.down("desktop")]: {
                      fontSize: "24px",
                    },
                  }}
                />
                {t("navPrevious")}
              </Button>
              <Button
                variant="text"
                onClick={listNavControls.navNext}
                disabled={!listNavState.canNavNext}
              >
                {t("navNext")}
                <NavNextIcon
                  sx={{
                    ...styles.iconGap,
                    fontSize: "32px",
                    [theme.breakpoints.down("desktop")]: {
                      fontSize: "24px",
                    },
                  }}
                />
              </Button>
            </>
          ) : null}
        </Box>
        {patientSelected ? (
          <Box sx={styles.outletContainer}>
            <Outlet />
          </Box>
        ) : (
          <Box sx={styles.emptyPromptContainer}>
            <img
              src={EmptyPage}
              style={{
                maxWidth: "60%",
                maxHeight: "60%",
                aspectRatio: 1.49,
              }}
              alt={t("emptyDetailPrompt")}
            />
            <Typography variant="body2" sx={styles.emptyPromptText}>
              {t("emptyDetailPrompt")}
            </Typography>
          </Box>
        )}
      </Box>
      <FeedbackDialog
        open={feedbackFormOpen}
        onSubmit={handleFeedbackOnSubmit}
        patientUid={patientSelected?.params.uid ?? ""}
      />
      <UnsavedChangesDialog
        open={unsavedChangesDialogInfo.open}
        onClose={handleUnsavedChangesDialogClose}
        onDiscard={handleUnsavedChangesDialogDiscard}
      />
    </Box>
  );
};

const styles = createStyleSheet({
  groupByPatientRow: {
    alignItems: "center",
    marginTop: 1,
  },
  container: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "row",
    overflowX: "hidden",
  },
  landscapeOnlyContainer: {
    p: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  leftPane: {
    transition: (theme) =>
      theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
    height: "100%",
    display: "flex",
    flexDirection: "column",
    position: "relative",
  },
  collapsedPaneContainer: {
    position: "absolute",
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    transition: (theme) =>
      theme.transitions.create("opacity", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
  },
  buttonContainer: {
    ...globalStyles.gapTop,
    marginBottom: 2,
  },
  paneContainer: {
    ...globalStyles.gapTop,
    px: 2,
    height: (theme) => `calc(100% - ${theme.spacing(2)})`,
    display: "flex",
    flexDirection: "column",
    transition: (theme) =>
      theme.transitions.create("opacity", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
    minWidth: (theme) => theme.spacing(56),
    [theme.breakpoints.down("desktop")]: {
      minWidth: theme.spacing(40),
    },
  },
  sortOrderText: {
    marginTop: 1,
    flex: 1,
    textAlign: "right",
  },
  taskListContainer: {
    flex: 1,
    overflowX: "hidden",
  },
  listContainer: {
    overflowY: "scroll",
    paddingY: 0,
  },
  topPane: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
  },
  navButtonContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    px: 3,
    height: (theme) => theme.spacing(5),
    backgroundColor: (theme) => theme.palette.detailBackground.main,
    [theme.breakpoints.down("desktop")]: {
      px: 2,
      height: (theme) => theme.spacing(4),
    },
  },
  outletContainer: {
    backgroundColor: (theme) => theme.palette.detailBackground.main,
    height: (theme) => `calc(100% - ${theme.spacing(5)})`,
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  emptyPromptContainer: {
    backgroundColor: (theme) => theme.palette.detailBackground.main,
    height: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyPromptText: {
    marginTop: 4,
    textAlign: "center",
  },
});

export default Patients;
